import { z } from 'zod'
import type { ReducedFrame } from './contract'
import { DroppedReason, Severity } from './contract'
import { viewTheme } from './theme'
import { useFrameDisplay } from './displayContext'
import { Info } from 'lucide-react'
import { createPortal } from 'react-dom'

type Meta = ReducedFrame['meta']

const DROPPED_REASON_HUMAN: Record<z.infer<typeof DroppedReason>, string> = {
  unresolved_dimension: 'missing the grouping attribute',
  missing_value: 'no value in this period',
  high_cardinality: 'too many distinct groups',
  page_cap: 'beyond the page limit',
  // I1: a deliberate top/bottom-N cut is not a cardinality overflow — the
  // reader asked for N and got N.
  ranked: 'outside the requested top/bottom N',
  having: 'removed by a measure condition',
}

/** SEN-1444: the mark kinds this shared annotation component actually draws —
 *  the load-bearing half of the marksConformance guard (marksConformance.test.ts):
 *  every frame renderer that mounts FrameAnnotations handles exactly these. */
export const ANNOTATION_MARK_KINDS = ['caption'] as const

/** SEN-1730: a note a RENDERER contributes — a fact the frame IMPLIES but does
 *  not carry, derived from the rows it was handed (e.g. a series whose last
 *  reading falls inside the requested period). It has no server `kind`, because
 *  no server produced it; it obeys the same tone rules as `meta.warnings` and
 *  lands in the same list, so a reader still looks in exactly one place. */
export type FrameNote = { human: string; severity: z.infer<typeof Severity> }

/** R8 (Task 9 fix round 1): the SAME text FrameAnnotations renders, joined
 *  one fact per line — for a renderer that visually clamps the banner (e.g.
 *  StatRenderer's fixed-height annotation region) to still hand the reader
 *  the full text via a `title` tooltip. One assembly, never a second copy
 *  that could drift from what actually renders. */
export function annotationFullText(meta: Meta, notes?: FrameNote[]): string {
  const warnings = [...(meta.warnings ?? []), ...(notes ?? [])]
  const dropped = (meta.dropped ?? []).filter((d) => d.count > 0)
  const captions = (meta.marks ?? []).filter(
    (m) => (ANNOTATION_MARK_KINDS as readonly string[]).includes(m.kind) && m.label,
  )
  return [
    ...captions.map((m) => m.label as string),
    ...warnings.map((w) => w.human),
    ...dropped.map((d) => `${d.count} excluded — ${DROPPED_REASON_HUMAN[d.reason] ?? d.reason}${d.detail ? ` (${d.detail})` : ''}`),
  ].join('\n')
}

/**
 * Shared annotation banners for any frame view (annotate-not-block, ADR-0028 §4).
 * Renders meta.warnings, renderer-contributed `notes`, and meta.dropped.
 * Tolerates null (legacy fixtures).
 */
export function FrameAnnotations({ meta, notes }: { meta: Meta; notes?: FrameNote[] }) {
  // The server's own warnings first, the renderer's derived notes after —
  // a caveat about the frame outranks a note about one series in it.
  const display = useFrameDisplay()
  const quiet = display.quiet ?? []
  const warnings = [...(meta.warnings ?? []), ...(notes ?? [])].filter((w) => !quiet.includes((w as { kind?: string }).kind ?? ''))
  const dropped = (meta.dropped ?? []).filter((d) => d.count > 0)
  // SEN-1411 (T6/F3): caption marks — agent-supplied explanations, rendered at
  // EVERY density ("the agent explains in place"). Info tone, never a warning.
  const captions = (meta.marks ?? []).filter(
    (m) => (ANNOTATION_MARK_KINDS as readonly string[]).includes(m.kind) && m.label,
  )
  if (warnings.length === 0 && dropped.length === 0 && captions.length === 0) return null
  if (display.notes === 'icon') {
    // The same facts, as the hover of one quiet mark (a host that asked for it).
    const text = annotationFullText({ ...meta, warnings: (meta.warnings ?? []).filter((w) => !quiet.includes(w.kind)) }, notes)
    const mark = (
      <span data-testid="view-notes-icon" title={text} aria-label={text} role="img"
        className="inline-flex w-fit cursor-help items-center text-muted-foreground/70 hover:text-muted-foreground">
        <Info className="h-3.5 w-3.5" aria-hidden />
      </span>
    )
    return display.notesSlot ? createPortal(mark, display.notesSlot) : mark
  }
  return (
    <>
      {/* Every banner is a LIST, one fact per line (viewTheme.noteList):
          bare sibling spans ran two facts together into one sentence. */}
      {captions.length > 0 && (
        <div data-testid="view-captions" className={`${viewTheme.caption.className} ${viewTheme.noteList.className}`}>
          {captions.map((m, i) => <span key={i}>{m.label}</span>)}
        </div>
      )}
      {/* Tone is the note's own severity: an `info` note (a stated method,
          e.g. "shown as a time-mean") reads in the caption tone inside the
          same list; warnings stay amber — the reader tells a caveat from a
          fact without a second container. */}
      {warnings.length > 0 && (
        <div data-testid="view-warnings" className={`${viewTheme.warning.className} ${viewTheme.noteList.className}`}>
          {warnings.map((w, i) => (
            <span key={i} data-severity={w.severity} className={w.severity === 'info' ? viewTheme.noteTone.className : undefined}>{w.human}</span>
          ))}
        </div>
      )}
      {dropped.length > 0 && (
        <div data-testid="view-dropped" className={`${viewTheme.warning.className} ${viewTheme.noteList.className}`}>
          {dropped.map((d, i) => (
            <span key={i}>
              {d.count} excluded — {DROPPED_REASON_HUMAN[d.reason] ?? d.reason}
              {d.detail ? ` (${d.detail})` : ''}
            </span>
          ))}
        </div>
      )}
    </>
  )
}
